import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Shield, Eye, EyeOff, AlertCircle, Clock, Link2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { getProviderIcon, getStatusBadge } from './ZonesList/utils';

interface DNSZone {
  id: string;
  name: string;
  providerId: string;
  providerName: string;
  providerType?: string;
  status?: 'active' | 'inactive' | 'pending';
  recordCount?: number;
  lastModified?: string;
  visibility?: 'public' | 'private';
  dnssecEnabled?: boolean;
  recordCountLoading?: boolean;
  recordCountError?: string;
}

interface ZonesListProps {
  zones: DNSZone[];
  onZoneClick: (zone: DNSZone) => void;
  isLoading: boolean;
}

const ZonesList: React.FC<ZonesListProps> = ({ zones, onZoneClick, isLoading }) => {

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="card animate-pulse min-h-[280px]">
            <div className="p-8">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                  <div>
                    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded mb-3 w-40"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
                  </div>
                </div>
                <div className="w-16 h-6 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-12"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-28"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-36"></div>
                  <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
      {zones.map((zone, index) => (
        <motion.div
          key={zone.id}
          className="card hover:shadow-xl transition-all duration-300 cursor-pointer group min-h-[280px]"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          whileHover={{ scale: 1.03, y: -4 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => onZoneClick(zone)}
        >
          <div className="p-8">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl flex items-center justify-center group-hover:from-blue-200 group-hover:to-indigo-200 dark:group-hover:from-blue-800/50 dark:group-hover:to-indigo-800/50 transition-all duration-300">
                  <Globe className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors mb-2">
                    {zone.name}
                  </h3>
                  <div className="flex items-center space-x-2 text-sm">
                    <span className="text-lg">{getProviderIcon(zone.providerType)}</span>
                    <span className="truncate font-medium text-slate-600 dark:text-slate-400">
                      {zone.providerName || 'DNS Provider'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0">{getStatusBadge(zone.status)}</div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between text-base">
                <div className="flex items-center space-x-2 text-slate-600 dark:text-slate-400">
                  <Link2 className="w-5 h-5" />
                  <span className="font-medium">DNS Records</span>
                </div>
                <div className="text-right">
                  {zone.recordCountLoading ? (
                    <span className="text-sm text-slate-500 dark:text-slate-400">loading…</span>
                  ) : zone.recordCountError ? (
                    <span className="inline-flex items-center text-sm text-red-600 dark:text-red-400">
                      <AlertCircle className="w-4 h-4 mr-1" /> err
                    </span>
                  ) : (
                    <>
                      <span className="text-2xl font-bold text-slate-900 dark:text-white">
                        {zone.recordCount || 0}
                      </span>
                      <span className="text-sm text-slate-500 dark:text-slate-400 ml-1">
                        records
                      </span>
                    </>
                  )}
                </div>
              </div>

              {zone.lastModified && (
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2 text-slate-600 dark:text-slate-400">
                    <Clock className="w-4 h-4" />
                    <span>Modified</span>
                  </div>
                  <span className="font-medium text-slate-700 dark:text-slate-300">
                    {formatDistanceToNow(new Date(zone.lastModified), { addSuffix: true })}
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-2 text-slate-600 dark:text-slate-400">
                  {zone.visibility === 'private' ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                  <span>Visibility</span>
                </div>
                <span className="font-medium text-slate-900 dark:text-white capitalize">
                  {zone.visibility || 'Public'}
                </span>
              </div>

              {zone.dnssecEnabled !== undefined && (
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2 text-slate-600 dark:text-slate-400">
                    <Shield className="w-4 h-4" />
                    <span>DNSSEC</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    {zone.dnssecEnabled ? (
                      <span className="inline-flex items-center px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs font-medium">
                        Enabled
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400 rounded text-xs font-medium">
                        Disabled
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                <span className="text-base font-medium text-slate-600 dark:text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400">
                  Click to manage records
                </span>
                <div className="text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 transform group-hover:translate-x-1 transition-all">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default ZonesList;
